import React, { useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { Activity, Menu, X, ChevronDown } from "lucide-react";
import { useState } from "react";

// Drapeaux SVG inline (rendu fiable cross-platform, vs emoji 🇫🇷/🇬🇧/🇪🇸/🇩🇪
// qui ne s'affichent pas sur Windows par défaut).
const FlagFR = ({ className = "" }) => (
  <svg viewBox="0 0 3 2" className={className} aria-hidden="true">
    <rect width="1" height="2" x="0" fill="#0055A4" />
    <rect width="1" height="2" x="1" fill="#FFFFFF" />
    <rect width="1" height="2" x="2" fill="#EF4135" />
  </svg>
);
const FlagGB = ({ className = "" }) => (
  <svg viewBox="0 0 60 30" className={className} aria-hidden="true">
    <clipPath id="t-gb"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
    <rect width="60" height="30" fill="#012169" />
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#FFF" strokeWidth="6" />
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4" clipPath="url(#t-gb)" />
    <path d="M30,0 v30 M0,15 h60" stroke="#FFF" strokeWidth="10" />
    <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
  </svg>
);
const FlagES = ({ className = "" }) => (
  <svg viewBox="0 0 3 2" className={className} aria-hidden="true">
    <rect width="3" height="2" fill="#AA151B" />
    <rect width="3" height="1" y="0.5" fill="#F1BF00" />
  </svg>
);
const FlagDE = ({ className = "" }) => (
  <svg viewBox="0 0 5 3" className={className} aria-hidden="true">
    <rect width="5" height="1" y="0" fill="#000000" />
    <rect width="5" height="1" y="1" fill="#DD0000" />
    <rect width="5" height="1" y="2" fill="#FFCE00" />
  </svg>
);

const LANGS = [
  { code: "fr", label: "Français", Flag: FlagFR },
  { code: "en", label: "English", Flag: FlagGB },
  { code: "es", label: "Español", Flag: FlagES },
  { code: "de", label: "Deutsch", Flag: FlagDE },
];

function LangDropdown({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  const Cur = current.Flag;
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md btn-ghost"
        data-testid="header-lang-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
      >
        <Cur className="h-3.5 w-5 rounded-[1px] ring-1 ring-white/20" />
        <span className="font-mono uppercase">{lang}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-1.5 w-24 py-1 rounded-lg bg-[#0a0a10]/95 backdrop-blur-xl border border-white/10 shadow-2xl z-50"
          data-testid="header-lang-menu"
        >
          {LANGS.map(({ code, label, Flag }) => (
            <li key={code}>
              <button
                onClick={() => { setLang(code); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-white/[0.06] transition ${
                  code === lang ? "text-cyan-300" : "text-zinc-200"
                }`}
                data-testid={`header-lang-${code}`}
                role="option"
                aria-selected={code === lang}
                aria-label={label}
              >
                <Flag className="h-3.5 w-5 rounded-[1px] ring-1 ring-white/20" />
                <span className="font-mono uppercase">{code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Header() {
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/features", label: t("nav.features") },
    { to: "/pricing", label: t("nav.pricing") },
    { to: "/tutorials", label: t("nav.tutorials") },
    { to: "/affiliates", label: t("nav.affiliates") },
    { to: "/copytrading", label: t("nav.copytrading") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#05050A]/80 border-b border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="header-logo">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-cyan-400 to-cyan-600 grid place-items-center">
            <Activity className="h-5 w-5 text-[#05050A]" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold tracking-tight">Dr Trade</div>
            <div className="text-[10px] text-cyan tracking-[0.2em] uppercase font-mono">Scalper Pro</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="header-nav-desktop">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-sm transition-colors ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`
              }
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangDropdown lang={lang} setLang={setLang} />
          <button
            onClick={() => navigate("/pricing")}
            className="hidden sm:inline-flex btn-primary px-4 py-2 rounded-lg text-sm"
            data-testid="header-cta-buy"
          >
            {t("nav.cta")}
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden btn-ghost p-2 rounded-md"
            data-testid="header-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/[0.06] bg-[#05050A]" data-testid="header-mobile-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm ${isActive ? "bg-white/[0.04] text-white" : "text-zinc-400"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
